import React, { useState } from "react";
import { toast } from "sonner";
import { Instagram, Mail } from "lucide-react";
import { api } from "../lib/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent", { description: "We'll reply within 2 business days." });
    } catch (err) {
      toast.error("Could not send message", { description: err?.response?.data?.detail || "Please try again." });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-2 gap-16">
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-gold mb-6">Say Hello</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">Let's<br/><span className="italic">connect.</span></h1>
          <div className="gold-divider my-8" />
          <p className="text-lg text-matte/75 font-light leading-relaxed max-w-lg">
            Questions, press, partnerships, or simply a beautiful hello — we'd love to hear from you.
          </p>
          <div className="mt-10 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-luxe text-gold mb-2">Email</div>
              <a data-testid="contact-email" href="mailto:crownedessence5@gmail.com" className="text-lg text-matte hover:text-gold link-gold">crownedessence5@gmail.com</a>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-luxe text-gold mb-2">Instagram</div>
              <a data-testid="contact-instagram" href="https://instagram.com/crownedessence5" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-lg text-matte hover:text-gold link-gold">
                <Instagram size={16} /> @crownedessence5
              </a>
            </div>
          </div>
        </div>

        <div>
          {done ? (
            <div data-testid="contact-success" className="border border-gold p-10 bg-cream">
              <div className="text-[11px] uppercase tracking-luxe text-gold">Received</div>
              <div className="mt-3 font-serif text-3xl">Thank you.</div>
              <p className="mt-3 text-matte/70">Your message is with us. We'll follow up personally within 2 business days.</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
              <F label="Your Name" testId="contact-name" required value={form.name} onChange={upd("name")} />
              <F label="Email" type="email" testId="contact-email-input" required value={form.email} onChange={upd("email")} />
              <F label="Subject" testId="contact-subject" value={form.subject} onChange={upd("subject")} />
              <div>
                <label className="block text-[10px] uppercase tracking-luxe-sm text-matte/60 mb-2">Message<span className="text-gold">*</span></label>
                <textarea
                  data-testid="contact-message"
                  required rows={6}
                  value={form.message}
                  onChange={upd("message")}
                  className="w-full bg-transparent border-b border-matte/30 py-3 outline-none focus:border-gold text-matte"
                />
              </div>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="px-10 py-4 bg-matte text-gold border border-gold/40 text-[11px] uppercase tracking-luxe-sm hover:bg-black transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function F({ label, required, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-luxe-sm text-matte/60 mb-2">{label}{required && <span className="text-gold">*</span>}</label>
      <input
        data-testid={testId}
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-matte/30 py-3 outline-none focus:border-gold text-matte"
      />
    </div>
  );
}
